<script setup>
import { ref, computed, getCurrentInstance } from 'vue';
import { Head, router } from '@inertiajs/vue3';
import MainLayout from '@/layout/main/MainLayout.vue';
import SimpleOverviewSection from '@/components/overview/SimpleOverviewSection.vue';
import Button from 'primevue/button';
import { useLoading } from '@/composables/useLoading';
import Analytics from '@/views/linkarme/Admin/Analytics.vue';
import RecommendedSitesSection from '@/components/dashboard/RecommendedSitesSection.vue';

const props = defineProps({
    coins: Object,
    notifications: Array,
    orders: Number,
    ordersPercent: Number,
    ordersIncrease: Boolean,
    usedCount: Number,
    usedCountPercent: Number,
    usedCountIncrease: Boolean,
    unusedCount: Number,
    unusedCountPercent: Number,
    unusedCountIncrease: Boolean,

    approvedCount: Number,
    approvedCountPercent: Number,
    approvedCountIncrease: Boolean,

    news: Array,
    newsCount: Number,
    recommended: Array,
    topSellers: Array,
    favorites: Array,
    interests: Array,
    projects: Array,
    projectsPercent: Number,
    projectsIncrease: Boolean,
    sitesByCountry: Array,
    sitesByCountrySvg: Array,
    overviewBanner: Object,
    overviewNews: Array,
});

const { proxy } = getCurrentInstance();
const $t = proxy.$t;
const { showLoading, hideLoading } = useLoading();

// Dashboard metrics data
const totalValueData = ref([
    {
        title: $t('Total Orders'),
        value: props.orders,
        percent: Math.abs(props.ordersPercent),
        increase: props.ordersIncrease,
        icon: 'pi pi-shopping-cart',
        color: 'primary-active',
        datasets: [
            {
                label: 'Total Orders',
                data: [
                    { x: '2023-01', y: 20 },
                    { x: '2023-02', y: 38 },
                    { x: '2023-03', y: 25 },
                    { x: '2023-04', y: 12 },
                    { x: '2023-05', y: 48 },
                    { x: '2023-06', y: 25 },
                    { x: '2023-07', y: 31 },
                    { x: '2023-08', y: 18 },
                    { x: '2023-09', y: 34 },
                    { x: '2023-10', y: 41 },
                    { x: '2023-11', y: 37 },
                    { x: '2023-12', y: 24 }
                ],
                backgroundColor: 'primary-active',
                borderRadius: 4
            }
        ]
    },
    {
        title: $t('Sites Used'),
        value: props.usedCount,
        percent: Math.abs(props.usedCountPercent),
        increase: props.usedCountIncrease,
        icon: 'pi pi-check-circle',
        color: 'emerald-600',
        datasets: [
            {
                label: 'Sites Used',
                data: [
                    { x: '2023-01', y: 42 },
                    { x: '2023-02', y: 21 },
                    { x: '2023-03', y: 28 },
                    { x: '2023-04', y: 48 },
                    { x: '2023-05', y: 34 },
                    { x: '2023-06', y: 25 },
                    { x: '2023-07', y: 50 },
                    { x: '2023-08', y: 12 },
                    { x: '2023-09', y: 36 },
                    { x: '2023-10', y: 30 },
                    { x: '2023-11', y: 22 },
                    { x: '2023-12', y: 6 }
                ],
                backgroundColor: 'emerald-600',
                borderRadius: 4
            }
        ]
    },
    {
        title: $t('Available Sites'),
        value: props.approvedCount,
        percent: Math.abs(props.approvedCountPercent),
        increase: props.approvedCountPercent,
        icon: 'pi pi-globe',
        color: 'orange-600',
        datasets: [
            {
                label: 'Available Sites',
                data: [
                    { x: '2023-01', y: 150 },
                    { x: '2023-02', y: 320 },
                    { x: '2023-03', y: 280 },
                    { x: '2023-04', y: 110 },
                    { x: '2023-05', y: 460 },
                    { x: '2023-06', y: 280 },
                    { x: '2023-07', y: 520 },
                    { x: '2023-08', y: 320 },
                    { x: '2023-09', y: 460 },
                    { x: '2023-10', y: 280 },
                    { x: '2023-11', y: 520 },
                    { x: '2023-12', y: 340 }
                ],
                backgroundColor: 'orange-600',
                borderRadius: 4
            }
        ]
    },
    {
        title: $t('Active Projects'),
        value: props.projects.length,
        percent: Math.abs(props.projectsPercent),
        increase: props.projectsIncrease,
        icon: 'pi pi-folder',
        color: 'violet-600',
        datasets: [
            {
                label: 'Active Projects',
                data: [
                    { x: '2023-01', y: 28 },
                    { x: '2023-02', y: 39 },
                    { x: '2023-03', y: 52 },
                    { x: '2023-04', y: 32 },
                    { x: '2023-05', y: 46 },
                    { x: '2023-06', y: 48 },
                    { x: '2023-07', y: 21 },
                    { x: '2023-08', y: 32 },
                    { x: '2023-09', y: 46 },
                    { x: '2023-10', y: 28 },
                    { x: '2023-11', y: 11 },
                    { x: '2023-12', y: 24 }
                ],
                backgroundColor: 'violet-600',
                borderRadius: 4
            }
        ]
    }
]);

// Recent activity data
const recentActivity = computed(() => [
    ...props.news.map(site => ({
        id: site.id,
        title: site.url,
        description: $t('New site available'),
        time: new Date().toISOString(),
        icon: 'pi pi-plus-circle',
        iconClass: 'text-green-600',
        type: 'new-site'
    })),
    ...props.notifications.slice(0, 5).map(notification => ({
        id: notification.id,
        title: notification.data.title || $t('Notification'),
        description: notification.data.message || '',
        time: notification.created_at,
        icon: 'pi pi-bell',
        iconClass: 'text-blue-600',
        type: 'notification'
    }))
].slice(0, 8));

const quickStats = computed(() => [
    {
        label: $t('Favorites'),
        value: props.favorites.length,
        icon: 'pi pi-heart',
        color: 'text-red-500',
        bgColor: 'bg-red-50'
    },
    {
        label: $t('Interests'),
        value: props.interests.length,
        icon: 'pi pi-star',
        color: 'text-yellow-500',
        bgColor: 'bg-yellow-50'
    },
    {
        label: $t('New Sites'),
        value: props.newsCount ?? 0,
        icon: 'pi pi-sparkles',
        color: 'text-blue-500',
        bgColor: 'bg-blue-50'
    },
    {
        label: $t('Active Projects'),
        value: props.projects.length,
        icon: 'pi pi-folder',
        color: 'text-purple-500',
        bgColor: 'bg-purple-50'
    }
]);

defineOptions({
    layout: MainLayout,
});
</script>

<template>
    <Head :title="$t('Dashboard')" />

    <SimpleOverviewSection :banner="overviewBanner" :news="overviewNews" />

    <div class="grid grid-cols-12 gap-6 px-6 py-3">
        <!-- Key Metrics Cards -->
        <div class="col-span-12 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
            <div
                v-for="metric in totalValueData"
                :key="metric.title"
                class="bg-white rounded-xl p-6 shadow-sm border border-surface-200 hover:shadow-md transition-shadow"
            >
                <div class="flex items-center justify-between mb-4">
                    <div class="flex items-center space-x-3">
                        <div :class="[
                            'w-12 h-12 rounded-lg flex items-center justify-center',
                            metric.color === 'primary-active' ? 'bg-blue-100 text-blue-600' :
                            metric.color === 'emerald-600' ? 'bg-green-100 text-green-600' :
                            metric.color === 'orange-600' ? 'bg-orange-100 text-orange-600' :
                            'bg-purple-100 text-purple-600'
                        ]">
                            <i :class="metric.icon + ' text-xl'"></i>
                        </div>
                        <div>
                            <p class="text-sm text-surface-600 font-medium">{{ $t(metric.datasets[0].label) }}</p>
                            <p class="text-2xl font-bold text-surface-900">{{ metric.value.toLocaleString() }}</p>
                        </div>
                    </div>
                </div>
                <div class="flex items-center justify-between">
                    <div class="flex items-center space-x-2">
                        <span 
                            v-if="metric.percent === 0"
                            class="text-xs text-surface-500 mr-[-4px]"
                        >
                            {{ $t('No changes') }}
                        </span>

                        <span 
                            v-else
                            :class="[
                                'inline-flex items-center px-2 py-1 rounded-full text-xs font-medium',
                                metric.increase ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            ]"
                        >
                            <i :class="[
                                'mr-1 text-xs',
                                metric.increase ? 'pi pi-arrow-up' : 'pi pi-arrow-down'
                            ]"></i>
                            {{ metric.percent }}%
                        </span>
                        <span class="text-xs text-surface-500">{{ $t('this month') }}</span>
                    </div>
                </div>
            </div>
        </div>

        <!-- Quick Stats -->
        <div class="col-span-12 md:col-span-4">
            <div class="bg-white rounded-xl p-6 shadow-sm border border-surface-200 h-full flex flex-col">
                <h3 class="text-lg font-semibold text-surface-900 mb-4">{{ $t('Quick Stats') }}</h3>
                <div class="space-y-4 flex-1">
                    <div
                        v-for="stat in quickStats"
                        :key="stat.label"
                        class="flex items-center justify-between p-3 rounded-lg border border-surface-100"
                    >
                        <div class="flex items-center space-x-3">
                            <div :class="[stat.bgColor, 'p-2 rounded-lg']">
                                <i :class="[stat.icon, stat.color, 'text-lg']"></i>
                            </div>
                            <span class="font-medium text-surface-900">{{ stat.label }}</span>
                        </div>
                        <span class="font-bold text-lg text-surface-900">{{ stat.value }}</span>
                    </div>
                </div>
            </div>
        </div>

        <!-- Recent Activity -->
        <div class="col-span-12 md:col-span-8">
            <div class="bg-white rounded-xl p-6 shadow-sm border border-surface-200 h-full flex flex-col">
                <div class="flex items-center justify-between mb-4">
                    <h3 class="text-lg font-semibold text-surface-900">{{ $t('Recent Activity') }}</h3>
                    <Button
                        :label="$t('View all sites')"
                        severity="secondary"
                        text
                        size="small"
                    />
                </div>
                <div class="space-y-3 flex-1">
                    <div
                        v-for="activity in recentActivity"
                        :key="activity.id"
                        class="flex items-center space-x-3 p-3 rounded-lg hover:bg-surface-50 transition-colors"
                    >
                        <div class="flex-shrink-0">
                            <i :class="[activity.icon, activity.iconClass, 'text-lg']"></i>
                        </div>
                        <div class="flex-1 min-w-0">
                            <p class="font-medium text-surface-900 truncate">{{ activity.title }}</p>
                            <p class="text-sm text-surface-600 truncate">{{ activity.description }}</p>
                        </div>
                        <div class="flex-shrink-0 text-xs text-surface-500">
                            {{ new Date(activity.time).toLocaleDateString() }}
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Brand New Opportunities -->
        <div class="col-span-12 lg:col-span-4">
            <div class="bg-white rounded-xl p-6 shadow-sm border border-surface-200">
                <div class="flex items-center space-x-2 mb-4">
                    <div class="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center">
                        <i class="pi pi-sparkles text-teal-600 text-lg"></i>
                    </div>
                    <div>
                        <h3 class="text-lg font-semibold text-teal-500">{{ $t('Brand new opportunities') }}</h3>
                        <p class="text-sm text-surface-600">{{ $t('Latest additions onto our catalog') }}</p>
                    </div>
                </div>

                <div class="space-y-3">
                    <div
                        v-for="(site, index) in news.slice(0, 3)"
                        :key="index"
                        @click="() => {
                            showLoading($t('Loading sites...'));
                            const url = route('client.sites.index') + `?filter[url]=${encodeURIComponent(site.real_url || site.url)}&filter[gambling]=false`;
                            router.visit(url, {
                                onFinish: () => hideLoading(),
                                onError: () => hideLoading()
                            });
                        }"
                        class="flex items-center justify-between p-4 rounded-lg border border-surface-100 hover:border-primary hover:shadow-sm transition-all cursor-pointer"
                    >
                        <div class="flex-1">
                            <p class="font-medium text-surface-900 truncate mb-2">{{ site.real_url || site.url }}</p>
                            <div class="flex items-center space-x-2">
                                <span class="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full font-medium">
                                    DR: {{ site.dr ?? '-' }}
                                </span>
                                <span class="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full font-medium">
                                    DA: {{ site.da ?? '-' }}
                                </span>
                                <span v-if="site.traffic !== null && site.traffic !== undefined"
                                    class="text-xs bg-orange-100 text-orange-800 px-2 py-1 rounded-full font-medium">
                                    {{ $t('Traffic') }}: {{ site.traffic?.toLocaleString() || 0 }}
                                </span>
                            </div>
                        </div>
                        <div class="flex items-center space-x-3 ml-4">
                            <span v-if="site.price" class="font-bold text-lg text-primary">
                                ${{ site.price }}
                            </span>
                            <i class="pi pi-chevron-right text-surface-400"></i>
                        </div>
                    </div>
                </div>

                <div class="text-right mt-4">
                    <Button
                        :label="$t('View all new sites')"
                        severity="secondary"
                        text
                        size="small"
                        @click="() => {
                            showLoading($t('Loading sites...'));
                            router.visit(route('client.sites.index') + '?filter[new]=true&filter[gambling]=false&sort=-dr', {
                                onFinish: () => hideLoading(),
                                onError: () => hideLoading()
                            });
                        }"
                    />
                </div>
            </div>
        </div>

        <!-- Top Sellers -->
        <div class="col-span-12 lg:col-span-4">
            <RecommendedSitesSection
                :sites="topSellers"
                title="Top Sellers"
                subtitle="Websites that deserve your attention"
                :max-items="3"
                route-name="client.sites.show"
                browse-route="client.sites.index"
                browse-filter="filter[gambling]=false&sort=-orders_count"
            />
        </div>

        <!-- Recommended Sites -->
        <div class="col-span-12 lg:col-span-4">
            <RecommendedSitesSection
                :sites="recommended"
                title="Our team recommendations"
                subtitle="Carefully selected websites for you"
                :max-items="3"
                route-name="client.sites.show"
                browse-route="client.sites.index"
                browse-filter="filter[recommended]=true&filter[gambling]=false&sort=-dr"
            />
        </div>

        <!-- Active Projects -->
        <div class="col-span-12">
            <div class="bg-white rounded-xl p-6 shadow-sm border border-surface-200">
                <div class="flex items-center justify-between mb-4">
                    <h3 class="text-lg font-semibold text-surface-900">{{ $t('Active Projects') }}</h3>
                    <Button
                        :label="$t('Manage Projects')"
                        severity="secondary"
                        @click="() => {
                            showLoading($t('Loading...'));
                            router.visit(route('client.projects.index'), {
                                onFinish: () => hideLoading(),
                                onError: () => hideLoading()
                            });
                        }"
                        text
                        size="small"
                    />
                </div>
                <div class="space-y-4">
                    <div
                        v-for="project in projects.slice(0, 5)"
                        :key="project.id"
                        class="grid grid-cols-12 gap-4 p-4 border border-surface-100 rounded-lg hover:border-primary transition-colors"
                    >
                        <div class="col-span-12 lg:col-span-6">
                            <div class="flex items-center space-x-3">
                                <div class="w-12 h-12 rounded-lg flex items-center justify-center text-white font-bold flex-shrink-0"
                                    :style="{ background: project.color ? `${project.color}`: '#3B82F6' }">
                                    {{ project.sites_count }}
                                </div>
                                <div class="flex-1 min-w-0">
                                    <p class="font-semibold text-surface-900 truncate">{{ project.name }}</p>
                                    <p class="text-sm text-surface-600">
                                        {{ project.sites_count }} {{ $t('sites assigned') }}
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div class="col-span-12 lg:col-span-6">
                            <div v-if="project.sites && project.sites.length > 0" class="space-y-2">
                                <p class="text-xs font-medium text-surface-600 uppercase mb-2">{{ $t('Recent Sites') }}</p>
                                <div class="flex flex-wrap gap-2">
                                    <div
                                        v-for="site in project.sites.slice(0, 3)"
                                        :key="site.id"
                                        @click="() => {
                                            showLoading($t('Loading sites...'));
                                            const url = route('client.sites.index') + `?filter[url]=${encodeURIComponent(site.real_url || site.url)}&filter[gambling]=false`;
                                            router.visit(url, {
                                                onFinish: () => hideLoading(),
                                                onError: () => hideLoading()
                                            });
                                        }"
                                        class="flex-1 min-w-0 flex items-center justify-between p-2 bg-surface-50 rounded border border-surface-200 hover:border-primary hover:bg-white hover:shadow-sm transition-all cursor-pointer"
                                    >
                                        <div class="flex-1 min-w-0">
                                            <p class="text-sm font-medium text-surface-900 truncate">{{ site.real_url || site.url }}</p>
                                            <div class="flex items-center space-x-1 mt-1">
                                                <span class="text-xs bg-green-100 text-green-800 px-1.5 py-0.5 rounded">
                                                    DR: {{ site.dr ?? '-' }}
                                                </span>
                                                <span class="text-xs bg-blue-100 text-blue-800 px-1.5 py-0.5 rounded">
                                                    DA: {{ site.da ?? '-' }}
                                                </span>
                                            </div>
                                        </div>
                                        <i class="pi pi-chevron-right text-surface-400 text-xs ml-2 flex-shrink-0"></i>
                                    </div>
                                </div>
                            </div>
                            <div v-else class="flex items-center justify-center h-full">
                                <p class="text-sm text-surface-500 italic">{{ $t('No sites assigned yet') }}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Analytics Section -->
        <Analytics
            class="col-span-12"
            :sites-by-country="sitesByCountry"
        />

        <!-- Quick Actions -->
        <div class="col-span-12">
            <div class="bg-white rounded-xl p-6 shadow-sm border border-surface-200">
                <h3 class="text-lg font-semibold text-surface-900 mb-4">{{ $t('Quick Actions') }}</h3>
                <div class="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
                    <Button
                        :label="$t('Browse Sites')"
                        icon="pi pi-search"
                        class="flex-col h-20"
                        severity="secondary"
                        outlined
                        @click="() => {
                            showLoading($t('Loading...'));
                            router.visit(route('client.sites.index'), {
                                onFinish: () => hideLoading(),
                                onError: () => hideLoading()
                            });
                        }"

                    />
                    <Button
                        :label="$t('View Orders')"
                        icon="pi pi-shopping-cart"
                        class="flex-col h-20"
                        severity="secondary"
                        outlined
                        @click="() => {
                            showLoading($t('Loading...'));
                            router.visit(route('client.orders.index'), {
                                onFinish: () => hideLoading(),
                                onError: () => hideLoading()
                            });
                        }"
                    />
                    <Button
                        :label="$t('Manage Projects')"
                        icon="pi pi-folder"
                        class="flex-col h-20"
                        severity="secondary"
                        outlined
                        @click="() => {
                            showLoading($t('Loading...'));
                            router.visit(route('client.projects.index'), {
                                onFinish: () => hideLoading(),
                                onError: () => hideLoading()
                            });
                        }"
                    />
                    <Button
                        :label="$t('Favorites')"
                        icon="pi pi-heart"
                        class="flex-col h-20"
                        severity="secondary"
                        outlined
                        @click="() => {
                            showLoading($t('Loading...'));
                            router.visit(route('client.sites.list') + '?filter[favorites]=true', {
                                onFinish: () => hideLoading(),
                                onError: () => hideLoading()
                            });
                        }"
                    />
                    <Button
                        :label="$t('Interests')"
                        icon="pi pi-flag"
                        class="flex-col h-20"
                        severity="secondary"
                        outlined
                        @click="() => {
                            showLoading($t('Loading...'));
                            router.visit(route('client.sites.list') + '?filter[interests]=true', {
                                onFinish: () => hideLoading(),
                                onError: () => hideLoading()
                            });
                        }"
                    />
                </div>
            </div>
        </div>
    </div>
</template>
